import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow,
  Card,
  CardContent,
  Typography,
  Divider,
  TextField,
  useTheme,
  Grid,
  Autocomplete,
  Button,
} from "@mui/material";
import { SaveAlt } from "@mui/icons-material";
import { GET } from "../../Generic/CrudRequest";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import Icons from "../../../Utils/Icons";
import utc from "dayjs/plugin/utc";
import useExcelExport from "../../../Utils/useExcelExport";

export default function ReportDamageForInspector(props) {
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState("");
  const [column, setColumn] = React.useState(0);
  const [order, setOrder] = React.useState("asc");
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [locations, setLocations] = useState([]);
  const [idUbicacion, setIdUbicacion] = useState(0);
  const [fecha, setFecha] = useState(dayjs(new Date()));
  const [fechaFin, setFechaFin] = useState(dayjs(new Date()));
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState("");
  dayjs.extend(utc);
  const theme = useTheme();
  const { config } = props;
  const exportToExcel = useExcelExport();
  const handleExport = () => {
    const filename = `${t("damageReportByInspector.title")}.xlsx`
    exportToExcel(rows, columns, filename);
  };

  const columns = [
  { id: "nombreInspector", label: t("damageReportByInspector.inspectorName"), minWidth: "100px" },
  { id: "noInspector", label: t("damageReportByInspector.noInspector"), minWidth: "100px" },
  { id: "ubicacion", label: t("damageReportByInspector.location"), minWidth: "100px" },
  {
    id: "totalUnidadesInspeccionadas",
    label: t("damageReportByInspector.inspectedUnits"),
    minWidth: "100px",
  },
  { id: "totalUnidadesDanos", label: t("damageReportByInspector.unitsWithDamage"), minWidth: "100px" },
  { id: "totalDanos", label: t("damageReportByInspector.damage"), minWidth: "100px" },
];

  useEffect(() => {
    fetchDamages();
  }, [page, rowsPerPage, column, order]);

  const fetchDamages = async () => {
    const dataDamages = await GET(
      `${config.url}/api/Reportes/reporte-danos-registrados-inspector`,
      {
        start: page * rowsPerPage,
        length: rowsPerPage,
        fehcaInicio: fecha,
        fehcaFin: fechaFin,
        idUbicacion: idUbicacion,
        inspector: user,
        column: column,
        dir: order,
      }
    );

    setRows(dataDamages.resultado);
    setTotalRecords(dataDamages.totalRegistros);
  };
  const fetchLocations = async (location, currentPage, perPage) => {
    const dataLocations = await GET(`${config.url}/api/Location/select`, {
      busqueda: location,
      page: currentPage,
      perPage: perPage,
    });

    setLocations(dataLocations.result);
  };

  const fetchUsers = async (user) => {
    const dataUsers = await GET(`${config.url}/api/User/select/${user}`);

    setUsers(dataUsers);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleRequestSort = (property, columnNumber) => {
    const isAsc = orderBy === property && order === "asc";
    setColumn(columnNumber);
    setOrderBy(property);
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleSearch = () => {
    fetchDamages();
  };

  const handleClear = () => {
    setIdUbicacion(0);
    setLocations([]);
    setUsers([]);
    setFecha(dayjs(new Date()));
    setFechaFin(dayjs(new Date()));
  };

  const handleInputChange = (event, value) => {
    if (value.trim().length >= 3) {
      fetchLocations(value, 1, 10);
    } else {
      setLocations([]);
    }
  };

  const handleInputChangeUser = (event, value) => {
    if (value.trim().length >= 3) {
      fetchUsers(value);
    } else {
      setUsers([]);
    }
  };
  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/ReportesInspeccion"
            tooltip={t("buttons.back")}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {t("damageReportByInspector.title")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            alignItems: "auto",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                noOptionsText={t("noOptions")}
                getOptionLabel={(user) => `${user.nombre}`}
                onChange={(event, value) => {
                  if (value) {
                    setUser(value.idUsuario);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option && value && option.idUsuario === value.idUsuario
                }
                onInputChange={handleInputChangeUser}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("filters.inspectedBy")}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={locations}
                noOptionsText={t("noOptions")}
                getOptionLabel={(location) =>
                  `${location.nombre} ${location.descripcion} ${
                    location.ciudad ?? ""
                  } ${location.ubicacionTipo ?? ""}`
                }
                onChange={(event, value) => {
                  if (value) {
                    setIdUbicacion(value.idUbicacion);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  option && value && option.idUbicacion === value.idUbicacion
                }
                onInputChange={handleInputChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("filters.originLocation")}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label= {t("filters.startDate")}
                  value={fecha}
                  fullWidth
                  onChange={(newValue) => {
                    setFecha(dayjs(newValue));
                  }}
                  slots={{
                    textField: (params) => (
                      <TextField variant="standard" fullWidth {...params} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label={t("filters.endDate")}
                  value={fechaFin}
                  fullWidth
                  onChange={(newValue) => {
                    setFechaFin(dayjs(newValue));
                  }}
                  slots={{
                    textField: (params) => (
                      <TextField variant="standard" fullWidth {...params} />
                    ),
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Icons
              iconName="Search"
              fontSize="large"
              onClick={handleSearch}
              style={{ color: theme.palette.primary.main }}
            />
            <Icons
              iconName="Clear"
              fontSize="large"
              onClick={handleClear}
              style={{ color: theme.palette.primary.main }}
            />
          </Grid>
        </CardContent>
        <Paper
          sx={{
            maxWidth: "calc(100% - 32px)",
            margin: "auto",
            overflow: "hidden",
            boxShadow: "none",
            border: "none",
          }}
        >
          <TableContainer>
            <Button
              variant="contained"
              startIcon={<SaveAlt />}
              onClick={handleExport}
            >
             {t("buttons.exportExcel")}
            </Button>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      style={{ minWidth: column.minWidth, fontWeight: "bold" }}
                    >
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : "asc"}
                        onClick={() =>
                          handleRequestSort(column.id, columns.indexOf(column))
                        }
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      {t("pagination.noData")}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      {columns.map((column) => (
                        <TableCell key={column.id} align="left">
                          {row[column.id] !== null && row[column.id] !== "" ? (
                            row[column.id]
                          ) : (
                            <i>{t("pagination.notAvailable")}</i>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100, { value: -1, label: t("pagination.all") }]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("pagination.labelRowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              t("pagination.displayedRows", {
              from,
              to,
              count,
            })
      }
          />
        </Paper>
      </CardContent>
    </Card>
  );
}
